/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { Dispatch } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import {
    Blank,
    Effect,
    OperationFailed,
    Pagination,
    ResponseErrorHandler,
    ResponseSuccessHandler,
    S3File,
    Setter,
    VersionedPagination,
} from "./Interfaces";
import axios, { AxiosError, AxiosResponse } from "axios";
import { logoutHandler } from "../features/auth/authFunctions";
import { store } from "../app/store";
import { setHasTimeout } from "../features/auth/localConfigSlice";

/**
 * Constructs a function to handle application errors using a specific component's context dispatch.
 * @param dispatch The React dispatch of the current component context.
 * @param effects Additional side effects to execute. These are executed prior to exiting the application when the
 *  status code is 401.
 */
export function stdErrHandlerFactory(dispatch: Dispatch<AnyAction>, effects: Effect = () => {}) {
    return (error: AxiosError<any, any>) => {
        effects();

        // There was a network timeout.
        if (error.code === "ECONNABORTED") {
            dispatch(setHasTimeout());
        } else if (!error.response || error.response.status === 401) {
            logoutHandler(dispatch);
        }
    };
}

/**
 * Constructs a function to handle application errors using a specific component's context dispatch.
 * @param dispatch The React dispatch of the current component context.
 * @param effects Additional side effects to execute. These are executed prior to exiting the application when the
 *  status code is 401. The effects are handed the error so that custom logic can be applied and error messages can be
 *  interpreted. This uses the response model OperationFailed = {error: string}.
 */
export function customErrHandlerFactory(
    dispatch: Dispatch<AnyAction>,
    effects: Setter<AxiosError<OperationFailed, any>> = () => {}
) {
    return (error: AxiosError<any, any>) => {
        effects(error as AxiosError<OperationFailed, any>);

        // There was a network timeout.
        if (error.code === "ECONNABORTED") {
            dispatch(setHasTimeout());
        } else if (!error.response || error.response.status === 401) {
            logoutHandler(dispatch);
        }
    };
}

export function getHandler<ResponseType>(
    route: string,
    dispatch: Dispatch<AnyAction>,
    onSuccess: ResponseSuccessHandler,
    onError?: ResponseErrorHandler,
    setLoading?: Setter<boolean>
) {
    let token: string = store.getState().auth.token;

    // If the user is not authenticated, then ignore this request.
    if (token === "") {
        return;
    }

    const authHeaders = {
        Authorization: `Token ${token}`,
    };

    if (setLoading) {
        setLoading(true);
    }

    axios
        .get(`${process.env.REACT_APP_API_ENDPOINT}${route}`, {
            timeout: 30000,
            headers: authHeaders,
        })
        .then((response: AxiosResponse<ResponseType>): void => {
            if (onSuccess) {
                onSuccess(response);
            }
            if (setLoading) {
                setLoading(false);
            }
        })
        .catch((error): void => {
            if (onError) {
                onError(error);
            }
            if (setLoading) {
                setLoading(false);
            }
        });
}

export function getHandlerVersionCaching<ModelType>(
    route: string,
    version: string,
    dispatch: Dispatch<AnyAction>,
    onSuccess: ResponseSuccessHandler,
    onNoChange?: Effect,
    onError?: ResponseErrorHandler,
    setLoading?: Setter<boolean>
) {
    if (version) {
        route += `?version=${version}`;
    }

    getHandler<VersionedPagination<ModelType>>(
        route,
        dispatch,
        (response: AxiosResponse<VersionedPagination<ModelType>, any>) => {
            if (response.status === 200) {
                onSuccess(response);
            } else if (response.status === 204) {
                if (onNoChange) {
                    onNoChange();
                }
            }
        },
        onError,
        setLoading
    );
}

export function postHandlerAnonymous<RequestType, ResponseType>(
    route: string,
    payload: RequestType,
    dispatch: Dispatch<AnyAction>,
    onSuccess?: ResponseSuccessHandler,
    onError?: ResponseErrorHandler,
    setLoading?: Setter<boolean>
) {
    if (setLoading) {
        setLoading(true);
    }

    axios
        .post(`${process.env.REACT_APP_API_ENDPOINT}${route}`, payload as RequestType, { timeout: 30000 })
        .then((response: AxiosResponse<ResponseType, any>): void => {
            if (onSuccess) {
                onSuccess(response);
            }
            if (setLoading) {
                setLoading(false);
            }
        })
        .catch((error): void => {
            if (onError) {
                onError(error);
            }
            if (setLoading) {
                setLoading(false);
            }
        });
}

export function postHandler<RequestType, ResponseType>(
    route: string,
    payload: RequestType,
    dispatch: Dispatch<AnyAction>,
    onSuccess?: ResponseSuccessHandler,
    onError?: ResponseErrorHandler,
    setLoading?: Setter<boolean>
) {
    let token: string = store.getState().auth.token;

    // If the user is not authenticated, then ignore this request.
    if (token === "") {
        return;
    }

    const authHeaders = {
        Authorization: `Token ${token}`,
    };

    if (setLoading) {
        setLoading(true);
    }

    axios
        .post(`${process.env.REACT_APP_API_ENDPOINT}${route}`, payload as RequestType, {
            timeout: 30000,
            headers: authHeaders,
        })
        .then((response: AxiosResponse<ResponseType, any>): void => {
            if (onSuccess) {
                onSuccess(response);
            }
            if (setLoading) {
                setLoading(false);
            }
        })
        .catch((error): void => {
            if (onError) {
                onError(error);
            }
            if (setLoading) {
                setLoading(false);
            }
        });
}

export function deleteHandler<ResponseType>(
    route: string,
    dispatch: Dispatch<AnyAction>,
    onSuccess?: ResponseSuccessHandler,
    onError?: ResponseErrorHandler,
    setLoading?: Setter<boolean>
) {
    let token: string = store.getState().auth.token;

    // If the user is not authenticated, then ignore this request.
    if (token === "") {
        return;
    }

    const authHeaders = {
        Authorization: `Token ${token}`,
    };

    if (setLoading) {
        setLoading(true);
    }

    axios
        .delete(`${process.env.REACT_APP_API_ENDPOINT}${route}`, {
            timeout: 30000,
            headers: authHeaders,
        })
        .then((response: AxiosResponse<ResponseType>): void => {
            if (onSuccess) {
                onSuccess(response);
            }
            if (setLoading) {
                setLoading(false);
            }
        })
        .catch((error): void => {
            if (onError) {
                onError(error);
            }
            if (setLoading) {
                setLoading(false);
            }
        });
}

export function putHandler<ResponseType>(
    route: string,
    payload: FormData,
    additionalHeaders: object,
    dispatch: Dispatch<AnyAction>,
    onSuccess?: ResponseSuccessHandler,
    onError?: ResponseErrorHandler,
    setLoading?: Setter<boolean>
) {
    let token: string = store.getState().auth.token;

    // If the user is not authenticated, then ignore this request.
    if (token === "") {
        return;
    }

    const authHeaders = {
        Authorization: `Token ${token}`,
        ...additionalHeaders,
    };

    if (setLoading) {
        setLoading(true);
    }

    axios
        .put(`${process.env.REACT_APP_API_ENDPOINT}${route}`, payload, {
            // PUT Handler has a much longer timeout due to larger payloads.
            // This is currently a Band-Aid for the PDF Uploading.
            timeout: 60000,
            headers: authHeaders,
        })
        .then((response: AxiosResponse<ResponseType, any>): void => {
            if (onSuccess) {
                onSuccess(response);
            }
            if (setLoading) {
                setLoading(false);
            }
        })
        .catch((error): void => {
            if (onError) {
                onError(error);
            }
            if (setLoading) {
                setLoading(false);
            }
        });
}

export enum ModelName {
    TechnicianEntry = "tech-entry",
    RMA = "rma",
    Loss = "loss",
    InventoryTransfer = "inventory-transfer",
    AssetTransfer = "asset-transfer",
    MaterialTransfer = "material-transfer",
}

export function uploadAttachment(
    model: ModelName,
    key: number,
    fileName: string,
    contents: any,
    dispatch: Dispatch<AnyAction>,
    onAttachmentUploaded: Setter<string>
) {
    const FormData = require("form-data");

    // Create a new form instance
    const form = new FormData();

    form.append("upload_file", new Blob([contents, 0, contents.length]), fileName);

    putHandler<Blank>(
        `/api/attachments/upload/${model}/${key}`,
        form,
        { "Content-Type": "multipart/form-data" },
        dispatch,
        (response: AxiosResponse<Blank, any>) => {
            onAttachmentUploaded(fileName);
        },
        stdErrHandlerFactory(dispatch)
    );
}

export function updateAttachmentList(
    model: ModelName,
    key: number,
    dispatch: Dispatch<AnyAction>,
    setFilesData: Setter<S3File[]>,
    setLoading: Setter<boolean>
) {
    getHandler<Pagination<S3File>>(
        `/api/attachments/list/${model}/${key}`,
        dispatch,
        (response: AxiosResponse<Pagination<S3File>, any>) => {
            setFilesData(response.data.results);
        },
        stdErrHandlerFactory(dispatch, () => setFilesData([])),
        setLoading
    );
}

export function deleteAttachment(
    model: ModelName,
    key: number,
    fileName: string,
    dispatch: Dispatch<AnyAction>,
    onAttachmentDeleted: Effect
) {
    deleteHandler<Blank>(
        `/api/attachments/delete/${model}/${key}/${fileName}`,
        dispatch,
        (response: AxiosResponse<Blank, any>) => onAttachmentDeleted(),
        stdErrHandlerFactory(dispatch)
    );
}

/**
 * Returns an Axios GET request as a Promise that can be used by any query function. This is intended to work with
 * libraries such as React Query.
 *
 * @param route The endpoint route to access, including all URL and query parameters. The protocol and host are both
 *     inferred using the built-in environment variables.
 * @param timeout The request timeout, default is 30000ms.
 */
export function buildGetRequest<ResponseModel>(
    route: string,
    timeout: number = 30000
): Promise<AxiosResponse<ResponseModel, any>> {
    let token: string = store.getState().auth.token;

    const authHeaders = {
        Authorization: `Token ${token}`,
    };

    return axios.get(`${process.env.REACT_APP_API_ENDPOINT}${route}`, { timeout: timeout, headers: authHeaders });
}

/**
 * Returns an Axios POST request as a Promise that can be used by any mutation function. This is intended to work with
 * libraries such as React Query.
 *
 * @param route The endpoint route to access, including all URL and query parameters. The protocol and host are both
 *     inferred using the built-in environment variables.
 * @param payload Model of the data to POST.
 * @param timeout The request timeout, default is 30000ms.
 */
export function buildPostRequest<BodyModel, ResponseModel>(
    route: string,
    payload: BodyModel,
    timeout: number = 30000
): Promise<AxiosResponse<ResponseModel, any>> {
    let token: string = store.getState().auth.token;

    const authHeaders = {
        Authorization: `Token ${token}`,
    };

    return axios.post(`${process.env.REACT_APP_API_ENDPOINT}${route}`, payload, { timeout: timeout, headers: authHeaders });
}

/**
 * Returns an Axios DELETE request as a Promise that can be used by any mutation function. This is intended to work with
 * libraries such as React Query.
 *
 * @param route The endpoint route to access, including all URL and query parameters. The protocol and host are both
 *     inferred using the built-in environment variables.
 * @param timeout The request timeout, default is 30000ms.
 */
export function buildDeleteRequest<ResponseModel>(
    route: string,
    timeout: number = 30000
): Promise<AxiosResponse<ResponseModel, any>> {
    let token: string = store.getState().auth.token;

    const authHeaders = {
        Authorization: `Token ${token}`,
    };

    return axios.delete(`${process.env.REACT_APP_API_ENDPOINT}${route}`, { timeout: timeout, headers: authHeaders });
}
